import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Flame, Trophy, Target, TrendingUp, Zap, ArrowRight, Edit, Star, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';

const getBmiData = (bmi) => {
  if (!bmi) return null;
  if (bmi < 16.5) return { label: 'Denutrition', color: '#ef4444', emoji: '🔴', advice: 'Consultez un professionnel de sante pour un suivi adapte.' };
  if (bmi < 18.5) return { label: 'Maigreur', color: '#3b82f6', emoji: '🔵', advice: 'Un peu de renforcement musculaire et une alimentation equilibree vous feront du bien !' };
  if (bmi < 25) return { label: 'Forme optimale', color: '#22c55e', emoji: '🟢', advice: 'Vous etes dans la zone ideale ! Continuez comme ca, chaque course vous rend plus fort.' };
  if (bmi < 30) return { label: 'Surpoids leger', color: '#f59e0b', emoji: '🟡', advice: 'Quelques seances par semaine et un prochain evenement sportif peuvent tout changer !' };
  if (bmi < 35) return { label: 'A surveiller', color: '#f97316', emoji: '🟠', advice: 'Lancez-vous un defi : inscrivez-vous a un prochain evenement, meme une petite distance !' };
  return { label: 'Priorite sante', color: '#ef4444', emoji: '🔴', advice: 'Chaque pas compte. Commencez doucement et fixez-vous des objectifs progressifs.' };
};

const getLevel = (eventsCount, bmi) => {
  const hasBmi = bmi && bmi >= 18.5 && bmi < 25;
  const score = eventsCount * 10 + (hasBmi ? 20 : 0);
  if (score >= 80) return { name: 'Champion', icon: Trophy, color: 'text-amber-500', bg: 'bg-amber-50', border: 'border-amber-200', stars: 5 };
  if (score >= 50) return { name: 'Confirme', icon: Flame, color: 'text-orange-500', bg: 'bg-orange-50', border: 'border-orange-200', stars: 4 };
  if (score >= 30) return { name: 'Regulier', icon: TrendingUp, color: 'text-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200', stars: 3 };
  if (score >= 10) return { name: 'Motive', icon: Zap, color: 'text-sky-500', bg: 'bg-sky-50', border: 'border-sky-200', stars: 2 };
  return { name: 'Debutant', icon: Target, color: 'text-slate-400', bg: 'bg-slate-50', border: 'border-slate-200', stars: 1 };
};

const motivationalTips = [
  { title: 'Hydratation', text: 'Buvez au moins 2L d\'eau par jour, surtout les jours d\'entrainement.', icon: '💧' },
  { title: 'Sommeil', text: '7 a 8h de sommeil = meilleure recuperation et performance.', icon: '😴' },
  { title: 'Objectif', text: 'Inscrivez-vous a un evenement : avoir une date motive l\'entrainement !', icon: '🎯' },
  { title: 'Progressivite', text: 'Augmentez vos distances de 10% max par semaine pour eviter les blessures.', icon: '📈' },
  { title: 'Nutrition', text: 'Privilegiez les glucides complexes avant l\'effort et les proteines apres.', icon: '🥗' },
  { title: 'Regularity', text: '3 seances par semaine suffisent pour progresser durablement.', icon: '📅' },
];

const WellnessWidget = ({ profile, registrations = [], onEditProfile }) => {
  const [showTips, setShowTips] = useState(false);

  const w = profile?.weight_kg;
  const h = profile?.height_cm;
  const bmi = w && h && h > 0 ? parseFloat((w / ((h / 100) ** 2)).toFixed(1)) : null;
  const bmiData = getBmiData(bmi);

  const totalEvents = registrations.length;
  const level = getLevel(totalEvents, bmi);
  const LevelIcon = level.icon;

  // Next level progress
  const currentScore = totalEvents * 10 + (bmi && bmi >= 18.5 && bmi < 25 ? 20 : 0);
  const thresholds = [0, 10, 30, 50, 80];
  const currentThresholdIdx = thresholds.findIndex((t, i) => i === thresholds.length - 1 || currentScore < thresholds[i + 1]);
  const nextThreshold = thresholds[Math.min(currentThresholdIdx + 1, thresholds.length - 1)];
  const prevThreshold = thresholds[currentThresholdIdx];
  const progressPct = nextThreshold > prevThreshold ? Math.min(((currentScore - prevThreshold) / (nextThreshold - prevThreshold)) * 100, 100) : 100;

  const randomTip = motivationalTips[Math.floor(Date.now() / 86400000) % motivationalTips.length];

  return (
    <motion.div
      className="mb-8"
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="wellness-widget"
    >
      <div className="flex items-center gap-2 mb-4">
        <Heart className="w-5 h-5 text-rose-500" />
        <h3 className="font-heading font-bold text-sm uppercase tracking-wider">Mon Score Forme</h3>
      </div>

      <div className="bg-white border border-slate-200 overflow-hidden">
        {/* Top section - Level + Score */}
        <div className="flex flex-col md:flex-row">
          {/* Level badge */}
          <div className={`${level.bg} ${level.border} border-b md:border-b-0 md:border-r p-6 flex flex-col items-center justify-center md:w-52 flex-shrink-0`}>
            <div className={`w-16 h-16 rounded-full ${level.bg} border-2 ${level.border} flex items-center justify-center mb-2`}>
              <LevelIcon className={`w-8 h-8 ${level.color}`} />
            </div>
            <p className={`font-heading font-extrabold uppercase tracking-wider text-sm ${level.color}`}>{level.name}</p>
            <div className="flex gap-0.5 mt-1">
              {[1, 2, 3, 4, 5].map(s => (
                <Star key={s} className={`w-3.5 h-3.5 ${s <= level.stars ? level.color : 'text-slate-200'}`} fill={s <= level.stars ? 'currentColor' : 'none'} />
              ))}
            </div>
            {/* Progress to next level */}
            {progressPct < 100 && (
              <div className="w-full mt-3">
                <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-brand rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPct}%` }}
                    transition={{ duration: 1, delay: 0.3 }}
                  />
                </div>
                <p className="text-[9px] text-slate-400 text-center mt-1 font-heading uppercase">Prochain niveau</p>
              </div>
            )}
          </div>

          {/* Main content */}
          <div className="flex-1 p-6">
            {bmi ? (
              <div className="flex flex-col sm:flex-row gap-6">
                {/* BMI gauge */}
                <div className="text-center flex-shrink-0">
                  <div className="relative w-28 h-28 mx-auto">
                    {/* Circular progress */}
                    <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="42" fill="none" stroke="#f1f5f9" strokeWidth="8" />
                      <motion.circle
                        cx="50" cy="50" r="42" fill="none"
                        stroke={bmiData.color}
                        strokeWidth="8"
                        strokeLinecap="round"
                        strokeDasharray={`${2 * Math.PI * 42}`}
                        initial={{ strokeDashoffset: 2 * Math.PI * 42 }}
                        animate={{ strokeDashoffset: 2 * Math.PI * 42 * (1 - Math.min(bmi / 40, 1)) }}
                        transition={{ duration: 1.2, delay: 0.2 }}
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-2xl font-extrabold font-heading" style={{ color: bmiData.color }}>{bmi}</span>
                      <span className="text-[9px] font-heading uppercase text-slate-400">IMC</span>
                    </div>
                  </div>
                  <p className="font-heading font-bold uppercase text-xs mt-2" style={{ color: bmiData.color }}>
                    {bmiData.label}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{w} kg / {h} cm</p>
                </div>

                {/* Motivation & advice */}
                <div className="flex-1 space-y-3">
                  <div className="bg-slate-50 border border-slate-100 p-3 rounded-sm">
                    <p className="text-sm text-slate-600 leading-relaxed">
                      <span className="font-bold text-asphalt">{bmiData.emoji} </span>
                      {bmiData.advice}
                    </p>
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-slate-50 p-2 text-center">
                      <p className="font-heading font-extrabold text-lg text-asphalt">{totalEvents}</p>
                      <p className="text-[9px] font-heading uppercase text-slate-400">Evenements</p>
                    </div>
                    <div className="bg-slate-50 p-2 text-center">
                      <p className="font-heading font-extrabold text-lg text-asphalt">
                        {h ? (profile?.gender === 'F' ? (h - 100 - (h - 150) / 2.5).toFixed(0) : (h - 100 - (h - 150) / 4).toFixed(0)) : '—'}
                      </p>
                      <p className="text-[9px] font-heading uppercase text-slate-400">Poids ideal (kg)</p>
                    </div>
                    <div className="bg-slate-50 p-2 text-center">
                      <p className="font-heading font-extrabold text-lg text-asphalt">
                        {(() => {
                          if (!w || !h || !profile?.birth_date) return '—';
                          const age = Math.floor((new Date() - new Date(profile.birth_date)) / (365.25 * 24 * 60 * 60 * 1000));
                          if (profile.gender === 'F') return Math.round(447.593 + 9.247 * w + 3.098 * h - 4.330 * age);
                          return Math.round(88.362 + 13.397 * w + 4.799 * h - 5.677 * age);
                        })()}
                      </p>
                      <p className="text-[9px] font-heading uppercase text-slate-400">Metabolisme (kcal)</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* No BMI data - motivational onboarding */
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="relative w-28 h-28 flex-shrink-0">
                  <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#f1f5f9" strokeWidth="8" />
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#e2e8f0" strokeWidth="8" strokeDasharray="8 8" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl">🏃</span>
                    <span className="text-[9px] font-heading uppercase text-slate-400 mt-1">IMC</span>
                  </div>
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h4 className="font-heading font-bold uppercase text-sm text-asphalt mb-2">
                    Decouvrez votre score forme !
                  </h4>
                  <p className="text-slate-500 text-sm leading-relaxed mb-4">
                    Renseignez votre poids et taille pour debloquer votre IMC, votre poids ideal, 
                    votre metabolisme de base et des conseils personnalises.
                  </p>
                  <Button
                    variant="outline"
                    className="gap-2 text-xs border-brand text-brand hover:bg-brand hover:text-white"
                    onClick={onEditProfile}
                    data-testid="wellness-edit-btn"
                  >
                    <Edit className="w-3.5 h-3.5" /> Renseigner mes mensurations
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom - Daily tip + CTA */}
        <div className="border-t bg-slate-50 px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span>{randomTip.icon}</span>
            <span><strong className="text-asphalt">{randomTip.title} :</strong> {randomTip.text}</span>
          </div>
          <button
            onClick={() => setShowTips(!showTips)}
            className="flex items-center gap-1 text-xs font-heading font-bold uppercase tracking-wider text-brand hover:underline flex-shrink-0"
            data-testid="wellness-more-tips"
          >
            {showTips ? 'Masquer' : 'Plus de conseils'} <ChevronRight className={`w-3 h-3 transition-transform ${showTips ? 'rotate-90' : ''}`} />
          </button>
        </div>

        {/* Expanded tips */}
        <AnimatePresence>
          {showTips && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 p-6 pt-3">
                {motivationalTips.map((tip, idx) => (
                  <motion.div
                    key={tip.title}
                    className="bg-white border border-slate-100 p-3 flex items-start gap-2"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <span className="text-lg flex-shrink-0">{tip.icon}</span>
                    <div>
                      <p className="font-heading font-bold text-xs uppercase text-asphalt">{tip.title}</p>
                      <p className="text-xs text-slate-500 leading-relaxed">{tip.text}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default WellnessWidget;
